import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Search.css';

import config from '../config';
const API_URL = config.API_URL;

interface Repository {
    id: number;
    name: string;
    full_name: string;
    description: string;
    html_url: string;
    stargazers_count: number;
    forks_count: number;
    language: string;
    owner: {
        login: string;
        avatar_url: string;
    };
    topics: string[];
    updated_at: string;
}

interface SearchState {
    query: string;
    results: Repository[];
    totalCount: number;
    currentPage: number;
    loading: boolean;
    searched: boolean;
}

const Search: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [state, setState] = useState<SearchState>({
        query: searchParams.get('q') || '',
        results: [],
        totalCount: 0,
        currentPage: parseInt(searchParams.get('page') || '1'),
        loading: false,
        searched: false
    });

    const [inputValue, setInputValue] = useState(searchParams.get('q') || '');
    const token = localStorage.getItem('token');

    // Load results from URL params on mount or when params change
    useEffect(() => {
        const queryFromUrl = searchParams.get('q');
        const pageFromUrl = parseInt(searchParams.get('page') || '1');

        if (queryFromUrl) {
            setInputValue(queryFromUrl);
            performSearch(queryFromUrl, pageFromUrl);
        }
    }, [searchParams]);

    const performSearch = async (query: string, page: number = 1) => {
        if (!query.trim()) return;

        setState(prev => ({ ...prev, loading: true }));

        try {
            // GitHub Search API (external 3rd party API)
            const response = await axios.get(
                `https://api.github.com/search/repositories`,
                {
                    params: {
                        q: query,
                        sort: 'stars',
                        order: 'desc',
                        per_page: 10,
                        page: page
                    }
                }
            );

            setState({
                query,
                results: response.data.items,
                totalCount: response.data.total_count,
                currentPage: page,
                loading: false,
                searched: true
            });
        } catch (error) {
            console.error('Search failed:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                searched: true,
                results: [] 
            }));
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim()) return;

        // Update URL with search params (enables back/forward navigation)
        setSearchParams({ q: inputValue.trim(), page: '1' });
    };

    const handlePageChange = (newPage: number) => {
        setSearchParams({ q: state.query, page: newPage.toString() });
        window.scrollTo(0, 0);
    };

    const formatNumber = (num: number): string => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const totalPages = Math.min(Math.ceil(state.totalCount / 10), 100); // GitHub limits to 1000 results

    return (
        <div className="search-page">
            {/* Navigation */}
            <nav className="search-nav">
                <div className="nav-container">
                    <Link to="/" className="nav-logo">
                        <img src="/pngegg.png" alt="NU Connect" className="nav-logo-img" />
                        <span>NU Connect</span>
                    </Link>
                    <div className="nav-links">
                        <Link to="/" className="nav-link">Home</Link>
                        <Link to="/search" className="nav-link active">Discover</Link>
                        <Link to="/chat" className="nav-link">Chat</Link>
                        {token ? (
                            <Link to="/profile" className="nav-link">Profile</Link>
                        ) : (
                            <Link to="/login" className="nav-btn primary">Sign In</Link>
                        )}
                    </div>
                </div>
            </nav>

            <div className="search-container">
                {/* Search Header */}
                <div className="search-header">
                    <h1>Discover Resources</h1>
                    <p>Search GitHub for projects, tutorials, and learning resources</p>
                </div>

                {/* Search Form */}
                <form onSubmit={handleSearch} className="search-form">
                    <div className="search-input-wrapper">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Search repositories (e.g., react tutorial, python machine learning)"
                            className="search-input"
                        />
                        <button type="submit" className="search-btn" disabled={state.loading}>
                            {state.loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                    <div className="search-suggestions">
                        <span>Try:</span>
                        <button type="button" onClick={() => { setInputValue('react beginner'); }}>
                            react beginner
                        </button>
                        <button type="button" onClick={() => { setInputValue('python data science'); }}>
                            python data science
                        </button>
                        <button type="button" onClick={() => { setInputValue('javascript interview'); }}>
                            javascript interview
                        </button>
                        <button type="button" onClick={() => { setInputValue('machine learning'); }}>
                            machine learning
                        </button>
                    </div>
                </form>

                {/* Results Section */}
                {state.loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Searching GitHub...</p>
                    </div>
                ) : state.searched ? (
                    <div className="results-section">
                        {/* Results Header */}
                        <div className="results-header">
                            <h2>
                                {state.totalCount > 0 
                                    ? `Found ${formatNumber(state.totalCount)} repositories for "${state.query}"`
                                    : `No results found for "${state.query}"`
                                }
                            </h2>
                        </div>

                        {/* Results List */}
                        {state.results.length > 0 ? (
                            <>
                                <ul className="results-list">
                                    {state.results.map((repo) => (
                                        <li key={repo.id} className="result-card">
                                            <div className="result-main">
                                                <div className="result-header">
                                                    <img 
                                                        src={repo.owner.avatar_url} 
                                                        alt={repo.owner.login}
                                                        className="owner-avatar"
                                                    />
                                                    <div className="result-title-section">
                                                        <h3 className="result-title">
                                                            <Link to={`/details/${repo.id}?name=${encodeURIComponent(repo.full_name)}`}>
                                                                {repo.full_name}
                                                            </Link>
                                                        </h3>
                                                        <span className="result-owner">by {repo.owner.login}</span>
                                                    </div>
                                                </div>
                                                
                                                <p className="result-description">
                                                    {repo.description || 'No description available'}
                                                </p>

                                                <div className="result-meta">
                                                    {repo.language && (
                                                        <span className="meta-item language">
                                                            <span className="language-dot" style={{ 
                                                                background: getLanguageColor(repo.language) 
                                                            }}></span>
                                                            {repo.language}
                                                        </span>
                                                    )}
                                                    <span className="meta-item">
                                                        ⭐ {formatNumber(repo.stargazers_count)}
                                                    </span>
                                                    <span className="meta-item">
                                                        🍴 {formatNumber(repo.forks_count)}
                                                    </span>
                                                    <span className="meta-item">
                                                        Updated {formatDate(repo.updated_at)}
                                                    </span>
                                                </div>

                                                {repo.topics && repo.topics.length > 0 && (
                                                    <div className="result-topics">
                                                        {repo.topics.slice(0, 5).map((topic) => (
                                                            <span key={topic} className="topic-tag">
                                                                {topic}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="result-actions">
                                                <Link 
                                                    to={`/details/${repo.id}?name=${encodeURIComponent(repo.full_name)}`}
                                                    className="btn-details"
                                                >
                                                    View Details →
                                                </Link>
                                                <a 
                                                    href={repo.html_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-github"
                                                >
                                                    Open in GitHub ↗
                                                </a>
                                            </div>
                                        </li>
                                    ))}
                                </ul>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="pagination">
                                        <button 
                                            onClick={() => handlePageChange(state.currentPage - 1)}
                                            disabled={state.currentPage === 1}
                                            className="page-btn"
                                        >
                                            ← Previous
                                        </button>
                                        
                                        <div className="page-numbers">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (state.currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (state.currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = state.currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => handlePageChange(pageNum)}
                                                        className={`page-num ${state.currentPage === pageNum ? 'active' : ''}`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <button 
                                            onClick={() => handlePageChange(state.currentPage + 1)}
                                            disabled={state.currentPage === totalPages}
                                            className="page-btn"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-results">
                                <h3>No repositories found</h3>
                                <p>Try different keywords or check your spelling</p>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Initial State - No search yet */
                    <div className="initial-state">
                        <div className="initial-icon">🔍</div>
                        <h3>Search for Learning Resources</h3>
                        <p>Find open-source projects, tutorials, and code examples from GitHub</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// Helper function for language colors
function getLanguageColor(language: string): string {
    const colors: { [key: string]: string } = {
        JavaScript: '#f1e05a',
        TypeScript: '#3178c6',
        Python: '#3572A5',
        Java: '#b07219',
        'C++': '#f34b7d',
        C: '#555555',
        Ruby: '#701516',
        Go: '#00ADD8',
        Rust: '#dea584',
        Swift: '#ffac45',
        Kotlin: '#A97BFF',
        PHP: '#4F5D95',
        HTML: '#e34c26',
        CSS: '#563d7c',
        Shell: '#89e051'
    };
    return colors[language] || '#8b8b8b';
}

export default Search;